/**
 * Seatics helpers for the attendee native WebView.
 *
 * IMPORTANT — why this file is deliberately tiny.
 *
 * Seatics renders its mobile quantity sheet (".sea-precheckout-parent-active")
 * as a DESCENDANT of #list-ctn, right next to the tapped ticket row. It only
 * looks like a full-screen drawer because #list-ctn is `overflow: visible` with
 * no height cap, so the sheet escapes the list box.
 *
 * That means any `overflow`, `max-height`, `min-height`, `height`, `position`,
 * `transform`, or `contain` we put on #list-ctn (or #sea-mp) turns it into a
 * scroll/clip container and imprisons the sheet inside the list band: the
 * quantity circles and close control clip at the Show Map bar, the sheet crops
 * horizontally, and it scrolls with the list instead of the viewport.
 *
 * So the native WebView runs the SAME Seatics layout as mobile Safari. We only
 * add behavior that cannot become an ancestor clip for the sheet:
 *  1) Mark html[data-promotix-native-webview] to gate native-only rules.
 *  2) Toggle body[data-promotix-precheckout-open] so our own ticket-row
 *     compositor layers flatten under Seatics' overlay in WKWebView.
 *  3) Native only: mark the ticket-row branches that WKWebView composites over
 *     the sheet so CSS can suppress their paint via `visibility` — geometry
 *     untouched, so the sheet is never trapped.
 */

export const PRECHECKOUT_OPEN_BODY_ATTR = 'data-promotix-precheckout-open';
export const NATIVE_WEBVIEW_HTML_ATTR = 'data-promotix-native-webview';
export const NATIVE_SHEET_BLEED_ATTR = 'data-promotix-native-sheet-bleed';

export const TICKET_ROW_SELECTOR = '.venue-ticket-list-row-js';

export const PRECHECKOUT_PARENT_ACTIVE_SELECTOR = [
  '.sea-precheckout-parent-active',
  '[class*="precheckout-parent-active"]',
  '[class*="pre-checkout-parent-active"]',
].join(', ');

const normalizeLabel = text => String(text || '').replace(/\s+/g, ' ').trim();

const hostIsShown = el => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.hasAttribute('hidden')) return false;
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return true;
  }
  const style = window.getComputedStyle(el);
  if (!style) return true;
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const opacity = Number.parseFloat(style.opacity);
  if (Number.isFinite(opacity) && opacity === 0) return false;
  return true;
};

const hostLooksLikeOpenQuantitySheet = host => {
  if (!hostIsShown(host)) return false;
  const text = String(host.textContent || '').replace(/\s+/g, ' ');
  if (/how many tickets/i.test(text)) return true;
  const cta = host.querySelector('#pre-checkout-price-cta');
  return cta instanceof HTMLElement && hostIsShown(cta);
};

export const isSeaticsPrecheckoutUiOpen = (root = typeof document !== 'undefined' ? document : null) => {
  if (!root || typeof root.querySelectorAll !== 'function') return false;
  const hosts = root.querySelectorAll(PRECHECKOUT_PARENT_ACTIVE_SELECTOR);
  for (let i = 0; i < hosts.length; i += 1) {
    if (hostLooksLikeOpenQuantitySheet(hosts[i])) return true;
  }
  return false;
};

/** Attendee Expo / react-native-webview only — never marks Safari. */
export const markNativeWebViewIfNeeded = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const ua = String(window.navigator?.userAgent || '');
  const embedded =
    Boolean(window.ReactNativeWebView) ||
    Boolean(window.webkit?.messageHandlers?.ReactNativeWebView) ||
    /Expo|ExpoGo|; wv\)|WebView/i.test(ua);
  if (!embedded) return false;
  document.documentElement.setAttribute(NATIVE_WEBVIEW_HTML_ATTR, '1');
  return true;
};

export const isNativeWebViewMarked = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  return doc?.documentElement?.getAttribute(NATIVE_WEBVIEW_HTML_ATTR) === '1';
};

/** Reports Seatics' expanded-map state. Read-only; nothing here restyles the map. */
export const isSeaticsMobileMapExpanded = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll) return false;
  const scopes = [];
  ['sea-mp', 'tn-maps'].forEach(id => {
    const el = typeof doc.getElementById === 'function' ? doc.getElementById(id) : null;
    if (el) scopes.push(el);
  });
  for (let s = 0; s < scopes.length; s += 1) {
    const nodes = scopes[s].querySelectorAll('button, a, [role="button"], div, span, p');
    for (let i = 0; i < nodes.length; i += 1) {
      if (/^hide\s*map$/i.test(normalizeLabel(nodes[i].textContent))) return true;
    }
  }
  return false;
};

/**
 * Highest ancestor of the sheet's controls that still contains no ticket row —
 * i.e. the quantity card itself. Anchored on #pre-checkout-price-cta because
 * that button is unambiguously inside the card, with the "How many tickets?"
 * node as fallback. Returns null when no sheet is open.
 */
export const findOpenQuantitySheetCard = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll) return null;

  let host = null;
  const hosts = doc.querySelectorAll(PRECHECKOUT_PARENT_ACTIVE_SELECTOR);
  for (let i = 0; i < hosts.length; i += 1) {
    if (hostLooksLikeOpenQuantitySheet(hosts[i])) {
      host = hosts[i];
      break;
    }
  }
  if (!host) return null;

  let anchor = host.querySelector('#pre-checkout-price-cta');
  if (!(anchor instanceof HTMLElement)) {
    // Deepest node that still carries the heading text.
    const nodes = host.querySelectorAll('*');
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      if (/how many tickets/i.test(normalizeLabel(nodes[i].textContent))) {
        anchor = nodes[i];
        break;
      }
    }
  }
  if (!(anchor instanceof HTMLElement)) return null;

  let card = anchor;
  while (
    card.parentElement instanceof HTMLElement &&
    card.parentElement !== doc.body &&
    card.parentElement.id !== 'list-ctn' &&
    card.parentElement.id !== 'sea-mp' &&
    !card.parentElement.querySelector(TICKET_ROW_SELECTOR)
  ) {
    card = card.parentElement;
  }
  return card;
};

export const clearNativeSheetBleedGuard = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll(`[${NATIVE_SHEET_BLEED_ATTR}]`).forEach(el => {
    el.removeAttribute(NATIVE_SHEET_BLEED_ATTR);
  });
};

/**
 * Native WebView only. WKWebView promotes Seatics' ticket rows onto their own
 * compositor layers, so they paint over the quantity sheet no matter what the
 * z-order says — the rows that bleed through the bottom of the drawer. Mark the
 * row branches so CSS can stop them painting; `visibility` is used because it
 * suppresses paint on a promoted layer without altering any geometry (a height
 * or overflow change here would trap the sheet inside #list-ctn instead).
 *
 * Only branches that do not lead to the sheet card are marked, so the card and
 * every ancestor of it keep painting.
 */
export const syncNativeSheetBleedGuard = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll) return 0;
  if (typeof window !== 'undefined' && window.innerWidth >= 992) {
    clearNativeSheetBleedGuard(doc);
    return 0;
  }
  if (!markNativeWebViewIfNeeded() && !isNativeWebViewMarked(doc)) {
    clearNativeSheetBleedGuard(doc);
    return 0;
  }

  const card = findOpenQuantitySheetCard(doc);
  if (!card) {
    clearNativeSheetBleedGuard(doc);
    return 0;
  }

  const stale = new Set(Array.from(doc.querySelectorAll(`[${NATIVE_SHEET_BLEED_ATTR}]`)));
  let marked = 0;

  // Scope to the containers that actually hold ticket rows, so unrelated
  // Seatics chrome (notably its own scrim) keeps rendering.
  const rowParents = new Set();
  ['list-ctn', 'sea-mp'].forEach(id => {
    const scope = typeof doc.getElementById === 'function' ? doc.getElementById(id) : null;
    if (!scope) return;
    scope.querySelectorAll(TICKET_ROW_SELECTOR).forEach(row => {
      if (card.contains(row)) return;
      if (row.parentElement instanceof HTMLElement) rowParents.add(row.parentElement);
    });
  });

  rowParents.forEach(parent => {
    Array.from(parent.children).forEach(child => {
      if (!(child instanceof HTMLElement)) return;
      if (child === card || card.contains(child) || child.contains(card)) return;
      child.setAttribute(NATIVE_SHEET_BLEED_ATTR, '1');
      stale.delete(child);
      marked += 1;
    });
  });

  stale.forEach(el => el.removeAttribute(NATIVE_SHEET_BLEED_ATTR));
  return marked;
};

export const syncSeaticsPrecheckoutOpenAttr = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  const body = doc?.body;
  if (!(body instanceof HTMLElement)) return false;
  const open = isSeaticsPrecheckoutUiOpen(doc);
  if (open) {
    body.setAttribute(PRECHECKOUT_OPEN_BODY_ATTR, '1');
  } else {
    body.removeAttribute(PRECHECKOUT_OPEN_BODY_ATTR);
  }
  return open;
};

export const installSeaticsPrecheckoutWebViewFix = (cancelled = () => false) => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => {};
  }

  markNativeWebViewIfNeeded();

  let debounce = null;
  const run = () => {
    if (cancelled()) return;
    markNativeWebViewIfNeeded();
    syncSeaticsPrecheckoutOpenAttr(document);
    syncNativeSheetBleedGuard(document);
  };

  const observer = new MutationObserver(() => {
    if (cancelled()) return;
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(run, 16);
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'id'],
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', run);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', run);
    }
  }

  run();
  return () => {
    if (debounce) clearTimeout(debounce);
    observer.disconnect();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', run);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', run);
      }
    }
    if (document.body) {
      document.body.removeAttribute(PRECHECKOUT_OPEN_BODY_ATTR);
    }
    clearNativeSheetBleedGuard(document);
  };
};
